/**
 * PrescribingReferencePanel — RAG-backed drug reference shown as the doctor enters a medication. Fetches
 * curated dosing/interaction/contraindication references and (when a patient is in context) patient-specific
 * safety flags from the deterministic rule engine. Deterministic, ₹0 — no LLM call.
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Collapse, Paper, Stack, Typography } from '@mui/material';
import { BookOpen, ShieldAlert } from 'lucide-react';
import { aiRag, PrescribingReferenceResponse } from '../../lib/api/modules/aiFeatures';

interface Props {
  drug: string;
  patientId?: string;
}

export function PrescribingReferencePanel({ drug, patientId }: Props) {
  const [data, setData] = useState<PrescribingReferenceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const name = (drug || '').trim();
    if (name.length < 3) { setData(null); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setLoading(true);
      aiRag.prescribingReference(name, patientId)
        .then(res => setData(res.data ?? null))
        .finally(() => setLoading(false));
    }, 400); // debounce typing
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [drug, patientId]);

  const hasContent = !!data && ((data.references?.length ?? 0) > 0 || (data.patientFlags?.length ?? 0) > 0);

  return (
    <Collapse in={loading || hasContent} unmountOnExit>
      <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: 'action.hover' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <BookOpen size={15} />
          <Typography variant="caption" fontWeight={700}>Prescribing reference{data?.drug ? ` — ${data.drug}` : ''}</Typography>
          {loading && <CircularProgress size={12} />}
        </Stack>

        {data?.patientFlags?.length ? (
          <Alert severity="warning" icon={<ShieldAlert size={16} />} sx={{ py: 0.25, mb: 1 }}>
            <Stack spacing={0.25}>
              {data.patientFlags.map((f, i) => (
                <Typography key={i} variant="caption">{f}</Typography>
              ))}
            </Stack>
          </Alert>
        ) : null}

        {data?.references?.map((r, i) => (
          <Box key={i} sx={{ mb: 0.75 }}>
            <Chip size="small" label={r.title} sx={{ mb: 0.25, height: 20, fontSize: 11 }} />
            <Typography variant="caption" color="text.secondary" display="block">{r.content}</Typography>
          </Box>
        ))}

        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
          Reference only — verify against the full record; not a substitute for clinical judgement.
        </Typography>
      </Paper>
    </Collapse>
  );
}
